import React, { FC, Fragment, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { CognitoUser } from 'amazon-cognito-identity-js';

import { AuthenticationType } from 'api/public';
import { AuthState } from 'types/aws-amplify';
import { useAuth, useClient } from 'hooks';
import { isSignedOut } from 'helpers';
import Cookies from 'cookies';

import { IAuthContainerProps } from '../types';
import SignIn from './SignIn';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import RequireNewPassword from './RequireNewPassword';
import ConfirmSignUp from './ConfirmSignUp';
import Invitation from './Invitation';
import ClientSelect from './ClientSelect';
import RouteController from './RouteController';

const { REACT_APP_BASE_URL } = process.env;

const Layout: FC<RouteComponentProps> = () => {
  const [password, setPassword] = useState('');
  const client = useClient();
  const { data, update } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('error_description')) {
    const errorDescription = urlParams.get('error_description') as string;
    let subdomain = errorDescription.split('UserLinkedNoSignupNeeded;')[1];
    if (subdomain) {
      subdomain = subdomain.split(';')[0];
      location.replace('https://' + subdomain);
      return null;
    }
  }

  const userIsAuth = Cookies.userAuthType === AuthenticationType.Auth;
  const clientIsSSO =
    client && client.authentication_type === AuthenticationType.Sso && client.sso_configuration;
  const clientDirectedSSO = clientIsSSO && !Cookies.userAuthType;

  if (isSignedOut(data.state) && !userIsAuth && clientDirectedSSO) {
    window.location.assign(`${REACT_APP_BASE_URL}/?provider=${client.sso_configuration.provider}`);
    return null;
  }

  function getProps() {
    return {
      authData: {
        ...data,
        password,
        username: Cookies.userEmail,
      },
      authState: data.state,
      onStateChange(state: AuthState, user: CognitoUser) {
        update({ state, user });
      },
      updateUsername(newUserName: string) {
        Cookies.userEmail = newUserName;
      },
      updatePassword(newPassword: string) {
        setPassword(newPassword);
      },
    } as IAuthContainerProps;
  }

  function renderComponent() {
    let AuthComponent;

    switch (data.state) {
      case AuthState.signIn:
        AuthComponent = SignIn;
        break;
      case AuthState.signUp:
        AuthComponent = SignUp;
        break;
      case AuthState.forgotPassword:
        AuthComponent = ForgotPassword;
        break;
      case AuthState.requireNewPassword:
        AuthComponent = RequireNewPassword;
        break;
      case AuthState.confirmSignUp:
        AuthComponent = ConfirmSignUp;
        break;
      case AuthState.invitation:
        AuthComponent = Invitation;
        break;
      case AuthState.clientSelect:
        AuthComponent = ClientSelect;
        break;
      default:
        throw new Error('Unhandled AuthState in auth/containers/Layout.tsx');
    }

    return <AuthComponent {...getProps()} />;
  }

  return (
    <Fragment>
      {renderComponent()}
      <RouteController {...getProps()} />
    </Fragment>
  );
};

export default withRouter(Layout);
